// Lógica pura de la pestaña «Gasto» (sin React): partidas, periodos, formato de fechas y cifras, cuadre con lo cobrado
// y servicios con nombre humano. Todo lo calcula el servidor (libro diario); aquí solo se agrupa y se escribe para Pau.
import { t, type TKey } from '../../../i18n';
import { numero, SIN_VALOR } from './formato';
import type { GastoSala, MotorGastoSala } from './tipos';

/** Objetivo de Pau (07-10-2026): servir cuesta menos del 20 % de nuestra parte. */
export const OBJETIVO = 0.2;
/** Fin de la barra del titular: el objetivo queda justo en medio. */
export const TOPE_BARRA = 0.4;
/** Primer día del libro (1-6 oct: estimados con el consumo y la tarifa) y primer día con registro llamada a llamada. */
export const INICIO_LIBRO = '2026-10-01';
export const INICIO_REGISTRO = '2026-10-07';

/** Partidas que ve Pau: cada propósito que apunta Calypso cae en una. Uno nuevo cae en «otros» (nunca se esconde). */
export const GRUPOS = [
  { clave: 'conversacion', clase: 'bg-oficina-serie-1', fill: 'fill-oficina-serie-1', propositos: ['conversacion', 'reescritura'] },
  { clave: 'repeticion', clase: 'bg-oficina-serie-2', fill: 'fill-oficina-serie-2', propositos: ['repeticion'] },
  { clave: 'revisor', clase: 'bg-oficina-serie-3', fill: 'fill-oficina-serie-3', propositos: ['revisor'] },
  {
    clave: 'memoria',
    clase: 'bg-oficina-serie-4',
    fill: 'fill-oficina-serie-4',
    propositos: ['memoria', 'resumen', 'notas', 'notas_fan', 'customs', 'poke', 'masivo', 'memoria_resumenes_pokes'],
  },
  { clave: 'otros', clase: 'bg-oficina-serie-5', fill: 'fill-oficina-serie-5', propositos: [] as string[] },
] as const;
export type ClaveGrupo = (typeof GRUPOS)[number]['clave'];

export function agrupar(partidas: Record<string, number>): Record<ClaveGrupo, number> {
  const out = { conversacion: 0, repeticion: 0, revisor: 0, memoria: 0, otros: 0 } as Record<ClaveGrupo, number>;
  for (const [p, v] of Object.entries(partidas)) {
    const g = GRUPOS.find((x) => (x.propositos as readonly string[]).includes(p));
    out[g ? g.clave : 'otros'] += v;
  }
  return out;
}

export type Periodo = 'hoy' | 'ayer' | '7' | '30' | 'mes' | 'fechas';
export const PERIODOS: Periodo[] = ['hoy', 'ayer', '7', '30', 'mes'];

export const DIA = 86_400_000;
export const iso = (ms: number) => new Date(ms).toISOString().slice(0, 10);
export const sumarDias = (dia: string, n: number) => iso(Date.parse(`${dia}T00:00:00Z`) + n * DIA);

/** Fechas (UTC) de un periodo. «7»/«30» = los últimos N días cerrados. */
export function rangoDe(p: Periodo, ahora: number, fechas: { desde: string; hasta: string }): { desde: string; hasta: string } {
  const hoy = iso(ahora);
  switch (p) {
    case 'hoy':
      return { desde: hoy, hasta: hoy };
    case 'ayer':
      return { desde: iso(ahora - DIA), hasta: iso(ahora - DIA) };
    case '7':
      return { desde: iso(ahora - 7 * DIA), hasta: iso(ahora - DIA) };
    case '30':
      return { desde: iso(ahora - 30 * DIA), hasta: iso(ahora - DIA) };
    case 'mes':
      return { desde: `${hoy.slice(0, 8)}01`, hasta: hoy };
    case 'fechas':
      return fechas.desde <= fechas.hasta ? fechas : { desde: fechas.hasta, hasta: fechas.desde };
  }
}

/** Estado frente al objetivo: solo pinta la cifra del titular y su barra. */
export type Estado = 'bueno' | 'aviso' | 'malo' | 'neutro';
export function estadoDe(p: number | null): Estado {
  if (p === null) return 'neutro';
  return p <= OBJETIVO ? 'bueno' : p <= 0.3 ? 'aviso' : 'malo';
}
export const TEXTO_ESTADO: Record<Estado, string> = {
  bueno: 'text-oficina-trabajando',
  aviso: 'text-oficina-bloqueado',
  malo: 'text-oficina-error',
  neutro: 'text-oficina-texto',
};
export const FONDO_ESTADO: Record<Estado, string> = {
  bueno: 'bg-oficina-trabajando',
  aviso: 'bg-oficina-bloqueado',
  malo: 'bg-oficina-error',
  neutro: 'bg-oficina-texto3',
};

/** Dólares: sin decimales desde 10 $, con uno por debajo (0,1 $ no es «0 $»). */
export function usd(v: number): string {
  return `${numero(v, Math.abs(v) < 10 && v !== 0 ? 1 : 0)} $`;
}
/** % sin decimales (las diferencias van en puntos). */
export function pct(x: number | null): string {
  return x === null || !Number.isFinite(x) ? SIN_VALOR : `${numero(Math.round(x * 100))} %`;
}
/** «Nos queda» con el mismo redondeo que se ve: 797 $ − 223 $ = 574 $, no 575 $. */
export function queda(parte: number, gasto: number): string {
  if (Math.abs(parte) < 10 || Math.abs(gasto) < 10) return usd(parte - gasto);
  return usd(Math.round(parte) - Math.round(gasto));
}

const lista = (k: TKey) => t(k).split(' ');
/** «7 oct» (es) / «Oct 7» (en). */
export function fecha(dia: string): string {
  return t('salaControl.gasto.fecha', { dia: Number(dia.slice(8, 10)), mes: lista('salaControl.gasto.mesesCortos')[Number(dia.slice(5, 7)) - 1] ?? '' });
}
export function nombreMes(dia: string): string {
  return lista('salaControl.gasto.mesesLargos')[Number(dia.slice(5, 7)) - 1] ?? '';
}
/** «30 sep – 6 oct», «1–6 oct» o «6 oct». */
export function rangoFechas(desde: string, hasta: string): string {
  if (desde === hasta) return fecha(desde);
  if (desde.slice(0, 7) === hasta.slice(0, 7)) {
    return t('salaControl.gasto.rangoMismoMes', {
      desde: Number(desde.slice(8, 10)),
      hasta: Number(hasta.slice(8, 10)),
      mes: lista('salaControl.gasto.mesesCortos')[Number(desde.slice(5, 7)) - 1] ?? '',
    });
  }
  return t('salaControl.gasto.rango', { desde: fecha(desde), hasta: fecha(hasta) });
}
export const dosCifras = (h: number) => String(h).padStart(2, '0');
export const hora = (h: number) => `${dosCifras(h)}:00`;
/** Hora con fracción (12,37) → «12:22». */
export const horaMinuto = (h: number) => {
  const m = Math.round(h * 60);
  return `${dosCifras(Math.floor(m / 60))}:${dosCifras(m % 60)}`;
};

export type DiaGasto = GastoSala['porDia'][number];
/** Todos los días del periodo, en orden; los que no traen datos van como hueco (null). */
export function diasDe(foto: Pick<GastoSala, 'desde' | 'hasta' | 'porDia'>): Array<{ dia: string; d: DiaGasto | null }> {
  const por = new Map(foto.porDia.map((d) => [d.dia, d]));
  const out: Array<{ dia: string; d: DiaGasto | null }> = [];
  for (let d = foto.desde; d <= foto.hasta && out.length < 400; d = sumarDias(d, 1)) out.push({ dia: d, d: por.get(d) ?? null });
  return out;
}
export const pctDia = (d: DiaGasto) => (d.parte && d.parte > 0 ? d.total / d.parte : null);

/** El día con menos % (sin contar hoy, que va a medias). */
export function mejorDia(foto: GastoSala, hoy: string): { dia: string; p: number } | null {
  let mejor: { dia: string; p: number } | null = null;
  for (const d of foto.porDia) {
    const p = pctDia(d);
    if (p === null || d.dia === hoy) continue;
    if (!mejor || p < mejor.p) mejor = { dia: d.dia, p };
  }
  return mejor;
}

/** Rango de los días estimados del periodo («1–6 oct»), o null si no hay. */
export function rangoEstimados(foto: Pick<GastoSala, 'porDia'>): string | null {
  const est = foto.porDia.filter((d) => d.origen === 'estimado').map((d) => d.dia).sort();
  return est.length ? rangoFechas(est[0]!, est[est.length - 1]!) : null;
}

/** Nombre humano de un proveedor. */
export function nombreProveedor(p: string): string {
  const conocidos: Record<string, string> = { deepseek: 'DeepSeek', openrouter: 'OpenRouter', anthropic: 'Anthropic', google: 'Google' };
  return conocidos[p.toLowerCase()] ?? p.charAt(0).toUpperCase() + p.slice(1);
}

export interface CuadreProveedor {
  clave: string;
  proveedor: string;
  apuntado: number;
  real: number | null;
  diferencia: number | null;
  /** Lo calculado incluye horas estimadas. */
  estimado: boolean;
  /** Lo cobrado sale de la factura del proveedor (si no, del saldo). */
  factura: boolean;
  /** Huella de la cuenta (la menor de sus llaves). */
  cuenta: string;
  /** Número de cuenta dentro de su proveedor (1, 2…) cuando el proveedor tiene varias; null si es la única. */
  numero: number | null;
}
/** Lo apuntado frente a lo cobrado, por cuenta de verdad: las llaves que comparten saldo (mismo proveedor y mismo
 *  cobro) se suman juntas. El servidor ya agrupa; esto evita leer dos veces el mismo cobro si llega sin agrupar. */
export function cuadrePorProveedor(cuadre: GastoSala['cuadre']): CuadreProveedor[] {
  const out = new Map<string, CuadreProveedor>();
  for (const c of cuadre ?? []) {
    const clave = c.real === null ? `${c.proveedor}|${c.cuenta}` : `${c.proveedor}|${c.real.toFixed(4)}`;
    const a = out.get(clave) ?? { clave, proveedor: c.proveedor, apuntado: 0, real: c.real, diferencia: null, estimado: false, factura: false, cuenta: c.cuenta, numero: null };
    a.apuntado += c.apuntado;
    if (c.cuenta < a.cuenta) a.cuenta = c.cuenta;
    a.estimado ||= !!c.estimado;
    a.factura ||= c.fuente === 'factura';
    out.set(clave, a);
  }
  // 10-10 (Pau): dos cuentas de DeepSeek salían como dos líneas «DeepSeek ±x %» iguales. Se numeran por su huella (no
  // por lo que gastan): «cuenta 1» es la misma cuenta todos los días.
  const filas = [...out.values()];
  for (const c of filas) {
    const hermanas = filas.filter((x) => x.proveedor === c.proveedor).map((x) => x.cuenta).sort();
    c.numero = hermanas.length > 1 ? hermanas.indexOf(c.cuenta) + 1 : null;
  }
  return filas
    .map((c) => ({ ...c, diferencia: c.real !== null && c.real > 0 ? (c.apuntado - c.real) / c.real : null }))
    .sort((a, b) => b.apuntado - a.apuntado);
}

/** Nombre de una cuenta del cuadre: el del proveedor (o `base`), con «(cuenta N)» si el proveedor tiene varias. */
export function nombreCuadre(c: Pick<CuadreProveedor, 'proveedor' | 'numero'>, base = nombreProveedor(c.proveedor)): string {
  return c.numero === null ? base : t('salaControl.gasto.cuentaN', { proveedor: base, n: c.numero });
}
/** Cuadra (±5 %) o falta lo apuntado (> 10 % por debajo: la cuenta la comparten otros usos). */
export const CUADRA = 0.05;
export const COMPARTIDA = -0.1;
export const puntosDif = (d: number) => Math.max(1, Math.round(Math.abs(d) * 100));

export type ClaveServicio = 'deepseek' | 'deepseekOr' | 'claude' | 'qwen' | 'gemini' | 'otros';
export function servicioDe(m: Pick<MotorGastoSala, 'motor' | 'proveedor'>): ClaveServicio {
  const mo = m.motor.toLowerCase();
  if (m.proveedor === 'deepseek') return 'deepseek';
  if (mo.startsWith('deepseek')) return 'deepseekOr';
  if (mo.startsWith('anthropic/') || mo.includes('claude')) return 'claude';
  if (mo.startsWith('qwen')) return 'qwen';
  if (mo.startsWith('google/') || mo.includes('gemini')) return 'gemini';
  return 'otros';
}
export interface FilaServicio {
  clave: ClaveServicio;
  usd: number;
  llamadas: number;
  precio: MotorGastoSala['precio'];
  motores: string[];
}
/** Modelos agrupados en servicios con nombre humano, de más a menos gasto. */
export function porServicio(motores: MotorGastoSala[]): FilaServicio[] {
  const out = new Map<ClaveServicio, FilaServicio>();
  for (const m of motores) {
    const k = servicioDe(m);
    const f = out.get(k) ?? { clave: k, usd: 0, llamadas: 0, precio: m.precio, motores: [] };
    f.usd += m.usd;
    f.llamadas += m.llamadas;
    if (f.precio !== m.precio) f.precio = 'mixto';
    f.motores.push(m.motor);
    out.set(k, f);
  }
  return [...out.values()].sort((a, b) => b.usd - a.usd);
}

/** «HH:MM» de un sello «AAAA-MM-DD HH:MM:SS…» (UTC). */
export const horaDeSello = (s: string | null) => (s && s.length >= 16 ? s.slice(11, 16) : null);
